/* Market-aware pagination: only Shopify-rendered cards and server-provided next URLs. */
if (!customElements.get('sd-infinite-collection')) {
  customElements.define('sd-infinite-collection', class extends HTMLElement {
    connectedCallback() {
      this.grid = this.parentElement.querySelector('#product-grid');
      this.status = this.querySelector('[data-status]');
      this.link = this.querySelector('[data-load]');
      this.next = this.dataset.next;
      this.visited = new Set();
      this.stopped = false;
      this.loading = false;
      this.click = (event) => { event.preventDefault(); this.failed = false; this.load(); };
      this.reset = () => { this.stopped = true; this.controller?.abort(); this.observer?.disconnect(); };
      this.link?.addEventListener('click', this.click);
      document.addEventListener('sd:collection-reset', this.reset);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting) && !this.failed) this.load();
        }, { rootMargin: '450px 0px' });
        if (this.next) this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.reset?.();
      this.link?.removeEventListener('click', this.click);
      document.removeEventListener('sd:collection-reset', this.reset);
    }

    requestURL(next) {
      const url = new URL(next, window.location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname) throw new Error('Unexpected collection URL');
      // Keep preview context when Shopify's pagination link omits it.
      const current = new URL(location.href);
      for (const key of ['preview_theme_id', '_fd', 'pb']) {
        if (current.searchParams.has(key) && !url.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      }
      url.hash = '';
      url.searchParams.set('section_id', this.dataset.section);
      return url;
    }

    async load() {
      if (this.loading || this.stopped || !this.next || !this.isConnected) return;
      this.loading = true;
      this.observer?.unobserve(this);
      this.setAttribute('aria-busy', 'true');
      if (this.link) this.link.hidden = true;
      this.status.textContent = 'Loading more products…';
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), 20000);
      try {
        const url = this.requestURL(this.next);
        if (this.visited.has(url.href)) throw new Error('Repeated pagination URL');
        const response = await fetch(url.href, {
          credentials: 'same-origin', cache: 'no-store', signal: this.controller.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok || response.redirected) throw new Error('Unable to load collection');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (this.stopped || !this.isConnected) return;
        const incoming = doc.querySelector('sd-infinite-collection');
        const grid = doc.querySelector('#product-grid');
        if (!incoming || !grid || incoming.dataset.section !== this.dataset.section) throw new Error('Invalid collection response');
        for (const key of ['country', 'currency', 'language']) {
          if (incoming.dataset[key] !== this.dataset[key]) throw new Error('Market context changed');
        }
        if (!(Number(incoming.dataset.page) > Number(this.dataset.page))) throw new Error('Pagination did not advance');
        const ids = new Set([...this.grid.querySelectorAll('[data-sd-product-id]')].map((card) => card.dataset.sdProductId));
        const cards = [...grid.querySelectorAll(':scope > [data-sd-product-id]')];
        let added = 0;
        for (const card of cards) {
          if (ids.has(card.dataset.sdProductId)) continue;
          ids.add(card.dataset.sdProductId);
          // Appended cards must remain visible without Dawn's initial animation observer.
          card.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
          if (this.grid.tagName !== 'UL') {
            const replacement = grid.cloneNode(false);
            this.grid.replaceWith(replacement);
            this.grid = replacement;
          }
          this.grid.append(card);
          added++;
        }
        this.visited.add(url.href);
        this.dataset.page = incoming.dataset.page;
        this.next = incoming.dataset.next || '';
        this.dataset.next = this.next;
        if (this.link) {
          if (this.next) this.link.href = this.next;
          this.link.textContent = 'Load more';
          this.link.hidden = !this.next;
        }
        this.status.textContent = this.next ? (added ? `${added} more products loaded.` : 'Checking for more products…') : 'All available products loaded.';
        // A short/empty market-specific page is NOT the end. Only Shopify's next URL decides.
        if (this.next) this.observer?.observe(this);
      } catch (error) {
        if (!this.stopped && this.isConnected) {
          this.failed = true;
          this.status.textContent = 'Products could not load. Please try again.';
          if (this.link) { this.link.hidden = false; this.link.textContent = 'Try again'; }
        }
      } finally {
        clearTimeout(timeout);
        this.loading = false;
        this.removeAttribute('aria-busy');
      }
    }
  });
}
